<script setup lang="ts">
import type { ChatAgentHistoryItemData } from '@dashhudson/dashing-ui/chat-agent-history';
import { ChatAgentHistory, extendedChatDateGroup } from '@dashhudson/dashing-ui/chat-agent-history';
import { computed, ref, watch } from 'vue';

import type { Conversation } from '@/components/VisionAi/chat-panel-components/utils/VisionChatSidePanel.types';
import { useDeleteConversationConfirmation } from '@/components/VisionAi/composables/useDeleteConversationConfirmation';
import RenameConversationPopup from '@/components/VisionAi/RenameConversationPopup.vue';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';

const visionAiStore = useVisionAiStore();
const visionAiConversationsStore = useVisionAiConversationsStore();

const selectedConversationId = ref(null);

const { confirmDeleteConversation } = useDeleteConversationConfirmation();

const conversations = computed(() =>
  (visionAiConversationsStore.conversations as Conversation[]).map((conversation) => ({
    id: conversation.id,
    title: conversation.name || 'New Chat',
    createdAt: Temporal.Instant.from(conversation.createdAt),
  })),
);

function handleNewChat() {
  visionAiStore.startNewChat();
  selectedConversationId.value = null;
}

function handleRename(item: ChatAgentHistoryItemData) {
  visionAiConversationsStore.openRenameConversationWidget({ conversationId: item.id });
}

function handleDelete(item: ChatAgentHistoryItemData) {
  confirmDeleteConversation({ conversationId: item.id, isSummary: false });
}

watch(selectedConversationId, (conversationId) => {
  if (conversationId) {
    visionAiConversationsStore.setCurrentConversationId(conversationId);
    visionAiConversationsStore.getConversationMessages({ conversationId });
  }
});

// Sync selection when the store's conversation ID is cleared externally (e.g. "New Chat" from menu)
watch(
  () => visionAiConversationsStore.currentConversationId,
  (storeId) => {
    if (storeId === null && selectedConversationId.value !== null) {
      selectedConversationId.value = null;
    }
  },
);
</script>

<template>
  <ChatAgentHistory
    v-model="selectedConversationId"
    :items="conversations"
    :group-by="extendedChatDateGroup"
    :storage-key="'vision-chat-history'"
    @new-chat="handleNewChat"
    @rename="handleRename"
    @delete="handleDelete"
  />
  <RenameConversationPopup />
</template>
